import { apiFetch } from "@/lib/api";

export type HomeDashboardPresentation = "count" | "list" | "chart" | "links";

export type HomeDashboardWidgetKey =
  | "incoming_orders"
  | "tracking_notifications"
  | "safety_stock_alerts"
  | "purchase_requests"
  | "support_tickets"
  | "favorite_links"
  | "serialized_devices_configured_this_week"
  | "ap_invoices_due_next_30_days";

export type HomeDashboardColumnSpan = 1 | 2 | 3 | 4;
export type HomeDashboardRowSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface HomeDashboardLayoutWidget {
  instanceId: string;
  widgetKey: HomeDashboardWidgetKey;
  presentation: HomeDashboardPresentation;
  order: number;
  /** How many of the three column slots this widget occupies. */
  columnSpan?: HomeDashboardColumnSpan;
  /** How many fixed-height row slots this widget occupies. */
  rowSpan?: HomeDashboardRowSpan;
  /** Zero-based column of the top-left cell. */
  col?: number;
  /** Zero-based row of the top-left cell. */
  row?: number;
  favoritePaths?: string[];
  /** Empty means every support-ticket status. */
  ticketStatuses?: string[];
  /** Defaults to the active (not archived) queue. */
  ticketQueue?: "active" | "archived" | "any";
  /** When true, only tickets waiting on the reporter. */
  ticketPendingFeedback?: boolean;
}

export interface HomeDashboardCatalogWidget {
  key: HomeDashboardWidgetKey;
  title: string;
  description: string;
  linkPath: string;
  presentations: HomeDashboardPresentation[];
  defaultPresentation: HomeDashboardPresentation;
}

export interface HomeDashboardWidgetPayload {
  instanceId: string;
  widgetKey: HomeDashboardWidgetKey;
  title: string;
  presentation: string;
  linkPath: string;
  count: number | null;
  list: { id: string; label: string; sublabel?: string | null; href?: string | null }[];
  series: { label: string; value: number }[];
  favoriteLinks: { path: string; label: string }[];
  error?: string;
}

export interface HomeDashboardTemplate {
  id: string;
  name: string;
  ownerUserId: string;
  ownerDisplayName: string | null;
  ownedByMe: boolean;
  widgets: HomeDashboardLayoutWidget[];
  createdAt: string;
  updatedAt: string;
}

export function fetchHomeDashboardCatalog() {
  return apiFetch<{ widgets: HomeDashboardCatalogWidget[] }>("/api/home-dashboard/catalog");
}

export function fetchHomeDashboardLayout() {
  return apiFetch<{ widgets: HomeDashboardLayoutWidget[] }>("/api/home-dashboard/layout");
}

export function saveHomeDashboardLayout(widgets: HomeDashboardLayoutWidget[]) {
  return apiFetch<{ widgets: HomeDashboardLayoutWidget[] }>("/api/home-dashboard/layout", {
    method: "PUT",
    body: { widgets },
  });
}

export function fetchHomeDashboardWidgets() {
  return apiFetch<{ widgets: HomeDashboardWidgetPayload[] }>("/api/home-dashboard/widgets");
}

export function fetchHomeDashboardTemplates() {
  return apiFetch<{ templates: HomeDashboardTemplate[] }>("/api/home-dashboard/templates");
}

export function createHomeDashboardTemplate(name: string, widgets?: HomeDashboardLayoutWidget[]) {
  return apiFetch<{ id: string; name: string; widgets: HomeDashboardLayoutWidget[] }>(
    "/api/home-dashboard/templates",
    {
      method: "POST",
      body: widgets != null ? { name, widgets } : { name },
    },
  );
}

export function updateHomeDashboardTemplate(
  id: string,
  body: { name?: string; widgets?: HomeDashboardLayoutWidget[] },
) {
  return apiFetch<{ ok: boolean }>(`/api/home-dashboard/templates/${encodeURIComponent(id)}`, {
    method: "PUT",
    body,
  });
}

export function applyHomeDashboardTemplate(id: string) {
  return apiFetch<{ widgets: HomeDashboardLayoutWidget[] }>(
    `/api/home-dashboard/templates/${encodeURIComponent(id)}/apply`,
    { method: "POST", body: {} },
  );
}
